import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiBaseUrl } from "../config/api";
import {
  validateResponse,
  type StudyObservationInput,
  type StudyObservationRecord,
  type StudyObservationResponse,
} from "../components/labeling/studyObservation";

export function useStudyObservation(projectId: string, studyUid: string, caseId: string) {
  const client = useQueryClient();
  const key = ["study-observation", projectId, studyUid, caseId];
  const url = `${getApiBaseUrl()}/api/labeling/projects/${encodeURIComponent(projectId)}/cases/${encodeURIComponent(studyUid)}/study-observation`;
  const query = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) => {
      const { data } = await axios.get<unknown>(url, { signal });
      validateResponse(data, projectId, studyUid, caseId);
      return data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
  const mutation = useMutation({
    mutationFn: async (input: StudyObservationInput) => {
      const { data } = await axios.put<{ success: boolean; record: StudyObservationRecord }>(
        url,
        input,
      );
      const current = query.data;
      if (!current || data.success !== true)
        throw new Error("Save could not be confirmed. Reload the study before retrying.");
      const next: StudyObservationResponse = { ...current, record: data.record };
      validateResponse(next, projectId, studyUid, caseId);
      client.setQueryData(key, next);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["project-cases", projectId] }),
        client.invalidateQueries({ queryKey: ["progress", projectId] }),
      ]);
      return next.record;
    },
  });
  return { query, mutation };
}
